import { Injectable } from "@angular/core";
import { RouteNotFoundError, type GuidId } from "../models";
import { assertTenantGuid, assertUserId, isGuid } from "../utils/helpers";

import { UserService } from "./user.service";
import { ApiService } from "./api";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class InitialiseService {
  constructor(
    private readonly us: UserService,
    private readonly api: ApiService
  ) {}

  /**
   * Check the user is logged in and has a userId available
   * @returns `true` if all is well, logs out otherwise
   */
  async user(): Promise<boolean> {
    try {
      // 1 - is user logged in?
      const loggedIn = await this.us.isLoggedIn();
      if (!loggedIn) {
        throw new Error("User not authenticated.");
      }

      // 2 - is the user id available?
      const userId = await this.us.getUserId();
      assertUserId(userId);

      return true;
    } catch (ex) {
      // assertions failed, logout and return to /login
      await this.us.logout();
      return false;
    }
  }

  /**
   * - Validates the route
   * - Check if route params are already loaded
   * - If not, checks user has permission to access route
   *
   * @param routeGuid the route to test
   * @returns {GuidId} the original route or fallback that the user has access to
   */
  async route(routeGuid?: GuidId): Promise<GuidId> {
    try {
      // 3 - is the current route valid?
      assertTenantGuid(routeGuid);

      // 4 - is the current tenant loaded?
      const resultRouteGuid = this.us.checkTenantReady(routeGuid);
      if (resultRouteGuid && isGuid(resultRouteGuid)) {
        return resultRouteGuid;
      }

      // 5 - tenant not loaded, check if user has access
      const userId = await this.us.getUserId();
      const result = await this.api.user.getTenantTier({
        userId: userId!,
        tenantGuid: routeGuid,
      });

      if (result.success) {
        const { tier, isAdmin, archived, defectLimit, defectCount, tenantId } =
          result.data;

        this.us.setUserProps({
          tier: tier,
          admin: isAdmin,
          enabled: !archived,
          defectLimit: defectLimit,
          defectCount: defectCount,
          tenantId: tenantId,
          tenantGuid: routeGuid,
        });

        return routeGuid;
      }
    } catch (ex) {
      if (!environment.production) console.log(ex);
      // assertions failed, will return to /login
      // `startNewRoute()` will then be called eventually logging the user out
      // if there's further errors
    }

    throw new RouteNotFoundError();
  }

  /**
   * Gets a users details and route permissions. Runs in:
   * - the token.guard for /login
   * - After register
   * - not used on /login but very similar
   * @returns {GuidId} the original route or fallback that the user has access to
   */
  async startNewRoute(): Promise<GuidId> {
    try {
      const loginResult = await this.api.login.userLogin();

      if (loginResult.success) {
        // sets default tenant here
        await this.us.handleLogin(loginResult.data);

        // load permissions
        const result = await this.api.user.getTenantTier({
          userId: loginResult.data.id,
          tenantGuid: this.us.tenantGuid(),
        });

        if (result.success) {
          const {
            tier,
            isAdmin,
            archived,
            defectLimit,
            defectCount,
            tenantId,
          } = result.data;

          this.us.setUserProps({
            tier: tier,
            admin: isAdmin,
            enabled: !archived,
            defectLimit: defectLimit,
            defectCount: defectCount,
            tenantId: tenantId,
            tenantGuid: this.us.tenantGuid(),
          });
        }

        const ids = await this.us.checkAndGetUserIds();

        if (ids.userId === null || ids.tenantId === null) {
          throw new Error("ids not loaded");
        }

        return this.us.tenantGuid() as GuidId;
      }
    } catch (ex) {
      console.log(ex);
      // assertions failed, logout and return to /login
      await this.us.logout();
    }

    throw new RouteNotFoundError();
  }
}
